import moment from 'moment';
import { DateFormats } from 'constants/common';
import {
  AgentActivityLog,
  AgentActivityLogOutcome,
  AgentActivityLogReview,
  AgentActivityLogReviewStatus,
} from '@trnsact/trnsact-shared-types';
import {
  ConfidenceBand,
  SmartIntakeHistoryOutcome,
  SmartIntakeHistoryRow,
  SmartIntakeRow,
  SmartIntakeStatus,
} from './SmartIntakeTypes';

const HIGH_CONFIDENCE_THRESHOLD = 0.85;
const PARTIAL_CONFIDENCE_THRESHOLD = 0.65;

export function confidenceColor(band: ConfidenceBand | null): string {
  if (band === 'high') return '#4caf50';
  if (band === 'partial') return '#ffa726';
  if (band === 'low') return '#ef5350';
  return 'text.secondary';
}

function toConfidenceBand(confidence: number | null): ConfidenceBand | null {
  if (confidence == null) return null;
  if (confidence >= HIGH_CONFIDENCE_THRESHOLD) return 'high';
  if (confidence >= PARTIAL_CONFIDENCE_THRESHOLD) return 'partial';
  return 'low';
}

function deriveStatus(
  outcome: AgentActivityLogOutcome | null | undefined,
  confidenceBand: ConfidenceBand | null,
  reviewReasons: string[]
): { status: SmartIntakeStatus; label: string } {
  if (outcome == null) return { status: 'parsing_in_progress', label: 'Parsing in progress' };
  if (outcome === AgentActivityLogOutcome.Failed) return { status: 'parsing_failed', label: 'Parsing failed' };

  const existingSubmissionSuffix = reviewReasons.includes('EXISTING_SUBMISSION_FOUND')
    ? ' — matching submission found'
    : '';

  if (reviewReasons.includes('EXISTING_OPPORTUNITY_FOUND'))
    return {
      status: 'partial',
      label: `Matching opportunity found — create submission in portal${existingSubmissionSuffix}`,
    };
  if (reviewReasons.includes('DEALER_NOT_MATCHED'))
    return { status: 'low', label: `Dealer not matched — selection required${existingSubmissionSuffix}` };
  if (reviewReasons.includes('NO_DYNAMICS_ID'))
    return { status: 'partial', label: `Created — CRM sync pending, review required${existingSubmissionSuffix}` };
  if (confidenceBand === 'high')
    return { status: 'high', label: `Parsed successfully — Approval required${existingSubmissionSuffix}` };
  if (confidenceBand === 'partial')
    return { status: 'partial', label: `Parsed partially — Manual review required${existingSubmissionSuffix}` };
  return { status: 'low', label: `Low confidence — Manual review required${existingSubmissionSuffix}` };
}

function deriveName(activity: AgentActivityLog): string {
  const extraction = (activity.payload as Record<string, unknown> | null)?.detailed_extraction as
    | Record<string, { value: unknown } | undefined>
    | null
    | undefined;
  const businessName = extraction?.businessName?.value;
  if (typeof businessName === 'string' && businessName.trim()) return businessName;

  const attachments = (activity.payload as Record<string, unknown> | null)?.attachments as
    | Array<{ fileName: string }>
    | null
    | undefined;
  if (Array.isArray(attachments) && attachments[0]?.fileName) return attachments[0].fileName;

  const trigger = activity.trigger as Record<string, unknown> | null;
  const fileName = trigger?.file_name as string | undefined;
  if (fileName) return fileName;

  return 'Untitled submission';
}

function deriveUploadedBy(activity: AgentActivityLog): string {
  const trigger = activity.trigger as Record<string, unknown> | null;
  return (
    (trigger?.user_uploaded_decision_letter as string | undefined) || (trigger?.email_from as string | undefined) || '—'
  );
}

export function mapAgentActivityToSmartIntakeRow(activity: AgentActivityLog): SmartIntakeRow {
  const trigger = activity.trigger as Record<string, unknown> | null;
  const confidence =
    (trigger?.extraction_confidence_score as number | undefined) ??
    (trigger?.orchestrator_confidence_score as number | undefined) ??
    null;
  const confidenceBand = toConfidenceBand(confidence);
  // reviewReasons isn't in @trnsact/trnsact-shared-types yet — read it defensively off the raw response.
  const reviewReasons = (activity as unknown as { reviewReasons?: string[] }).reviewReasons ?? [];
  const { status, label } = deriveStatus(activity.outcome, confidenceBand, reviewReasons);
  const createdDateTime = activity.createdDateTime ?? null;

  return {
    id: activity.agentActivityLogId,
    uploadDate: createdDateTime ? moment(createdDateTime).format(DateFormats.DateTimeShort12Hour) : null,
    uploadDateSortValue: createdDateTime ? moment(createdDateTime).valueOf() : 0,
    name: deriveName(activity),
    confidence,
    confidenceBand,
    status,
    statusLabel: label,
    uploadedBy: deriveUploadedBy(activity),
  };
}

// ── History ──────────────────────────────────────────────────────────────────

function deriveHistoryOutcome(review: AgentActivityLogReview): {
  outcome: SmartIntakeHistoryOutcome;
  label: string;
} {
  if (review.status === AgentActivityLogReviewStatus.Confirmed) return { outcome: 'confirmed', label: 'Confirmed' };
  if (review.status === AgentActivityLogReviewStatus.Rejected) return { outcome: 'rejected', label: 'Rejected' };
  // DISMISSED is only used for parsing-failed rows (see Dismiss action) — surface it as Failed.
  return { outcome: 'failed', label: 'Failed' };
}

export function buildSubmissionLink(
  review: AgentActivityLogReview | null
): { label: string; url: string | null } | null {
  if (!review) return null;
  const { vendorOpportunityId, creditSubmissionId } = review;
  if (!vendorOpportunityId && !creditSubmissionId) return null;
  if (vendorOpportunityId && creditSubmissionId) {
    return {
      label: 'Credit Submission',
      url: `/vo/${vendorOpportunityId}?creditSubmissionId=${creditSubmissionId}#submissions`,
    };
  }
  if (vendorOpportunityId) return { label: 'Vendor Opportunity', url: `/vo/${vendorOpportunityId}` };
  return { label: 'Credit Submission', url: null };
}

export function mapAgentActivityToSmartIntakeHistoryRow(activity: AgentActivityLog): SmartIntakeHistoryRow {
  const review = activity.review ?? null;
  const { outcome, label } = review ? deriveHistoryOutcome(review) : { outcome: 'failed' as const, label: 'Failed' };
  const submission = buildSubmissionLink(review);
  const createdDateTime = activity.createdDateTime ?? null;

  return {
    id: activity.agentActivityLogId,
    uploadDate: createdDateTime ? moment(createdDateTime).format(DateFormats.DateTimeShort12Hour) : null,
    uploadDateSortValue: createdDateTime ? moment(createdDateTime).valueOf() : 0,
    name: deriveName(activity),
    outcome,
    outcomeLabel: label,
    reviewedByName: review?.reviewedByName ?? '—',
    reviewedDate: review?.reviewedDateTime
      ? moment(review.reviewedDateTime).format(DateFormats.DateTimeShort12Hour)
      : null,
    submissionLabel: submission?.label ?? null,
    submissionUrl: submission?.url ?? null,
  };
}
